import React, { useEffect, useState } from "react";
import MetisMenu from "metismenujs";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logodark from "../../assets/images/logo.png";
import logosmdark from "../../assets/images/logo-sm-dark.png";
import mountlogo from "../../assets/images/mountlogo.png";

// Inline styles
const styles = {
  logo: {
    width: "100%",
    maxWidth: "200px",
    display: "block",
    margin: "0 auto 15px",
    marginTop: "10px",
  },
  footer: {
    color: "#000000",
    textAlign: "center",
    fontSize: "14px",
    marginTop: "auto",
    padding: "10px 0",
  },
};

const mobilePanelStyle = {
  position: "absolute",
  left: "217px", // sidebar width
  top: 0,
  width: "200px",
  backgroundColor: "#252b3b",
  height: "100vh",
  overflowY: "auto",
  zIndex: 200,
  padding: "10px",
};

const backButtonStyle = {
  color: "#ffffff",
  marginBottom: "10px",
  cursor: "pointer",
};

const SidebarContent = () => {
  const organization = useSelector((state) => state.organization);
  const location = useLocation();

  // Mobile and collapse state: on small devices, the sidebar is collapsed (shows only icons)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  // Holds child menu for mobile panel when an icon is clicked
  const [mobileChildren, setMobileChildren] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsCollapsed(mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // When in mobile mode, remove MetisMenu event listeners by cloning the side menu.
  useEffect(() => {
    if (isMobile) {
      const sideMenu = document.getElementById("side-menu");
      if (sideMenu) {
        // Remove the "metismenu" class and replace the node to drop MetisMenu listeners.
        sideMenu.classList.remove("metismenu");
        const cloned = sideMenu.cloneNode(true);
        sideMenu.parentNode.replaceChild(cloned, sideMenu);
      }
    }
  }, [isMobile]);

  // Get user info from local storage to control permissions
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const isSuperAdmin = user ? user.superadmin : false;
  const permissions = user && user.permissions ? user.permissions : [];

  // Initialize MetisMenu only for desktop mode
  useEffect(() => {
    if (!isMobile) {
      new MetisMenu("#side-menu");
    }
  }, [isMobile]);

  // ACTIVE COLORS
  const activeColor = "#0b1d51"; // blue used across components
  const defaultLabelColor = "#6b7280"; // muted gray for non-active labels
  const activeBackground = "rgba(13,71,161,0.04)"; // subtle accent (optional)

  // Helper: robust path match. Treat empty/#/null as not active.
  const pathMatches = (to) => {
    if (!to || typeof to !== "string") return false;
    // normalize - strip trailing slashes
    const norm = (str) => (str.endsWith("/") && str.length > 1 ? str.slice(0, -1) : str);
    const cur = norm(location.pathname || "/");
    const target = norm(to);
    if (target === cur) return true;
    // also consider startsWith for sections, e.g. /users and /users/123
    try {
      return cur.startsWith(target) && target !== "/";
    } catch {
      return false;
    }
  };

  // Recursively check if an item or any child matches current path
  const isActiveItem = (item) => {
    if (!item) return false;
    if (item.to && pathMatches(item.to)) return true;
    if (item.menu && Array.isArray(item.menu)) {
      for (const child of item.menu) {
        if (isActiveItem(child)) return true;
      }
    }
    if (item.children && Array.isArray(item.children)) {
      for (const child of item.children) {
        if (isActiveItem(child)) return true;
      }
    }
    return false;
  };

  // Render menu items without setting active states automatically.
  const renderMenuItems = (menu, depth = 0) => {
    return menu
      .filter((item) =>
        isSuperAdmin || permissions.some((perm) => perm.includes(item.id))
      )
      .map((item) => {
        // On mobile, for top-level items in collapsed sidebar, show no label.
        const showLabel = depth > 0 || !(isMobile && isCollapsed);
        // For top-level items on mobile collapsed, do not include dropdown arrow class.
        const linkClass =
          depth === 0 && isMobile && isCollapsed
            ? "waves-effect"
            : "has-arrow waves-effect";

        const active = isActiveItem(item);

        const labelStyle = {
          color: active ? activeColor : defaultLabelColor,
          fontWeight: active ? 700 : 500,
          display: "inline-block",
          verticalAlign: "middle",
        };

        const linkStyle = {
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: depth === 0 ? "10px 12px" : "8px 24px",
          background: active ? activeBackground : "transparent",
          borderLeft: active ? `3px solid ${activeColor}` : "3px solid transparent",
          textDecoration: "none",
        };

        // Render a child-list (sub-menu) when appropriate
        if (item.children && item.children.length > 0) {
          return (
            <li key={item.id}>
              <Link
                to="#"
                className={linkClass}
                aria-expanded={active ? "true" : "false"}
                onClick={(e) => {
                  // In mobile collapsed mode, clicking a top-level icon shows its children in the right panel.
                  if (isMobile && isCollapsed && depth === 0) {
                    e.preventDefault();
                    setMobileChildren(item.children);
                  }
                }}
                style={linkStyle}
                aria-current={active ? "page" : undefined}
              >
                <span style={{ display: "inline-flex", alignItems: "center", color: active ? activeColor : undefined }}>
                  {item.icon && item.icon}
                </span>
                {showLabel && <span className="ms-1" style={labelStyle}>{item.Label}</span>}
              </Link>
              {(!isMobile || !isCollapsed || depth > 0) && (
                <ul className="sub-menu">
                  {renderMenuItems(item.children, depth + 1)}
                </ul>
              )}
            </li>
          );
        } else {
          // leaf node
          return (
            <li key={item.id}>
              <Link to={item.to || "#"} className="waves-effect" style={linkStyle} aria-current={active ? "page" : undefined}>
                <span style={{ display: "inline-flex", alignItems: "center", color: active ? activeColor : undefined }}>
                  {item.icon && item.icon}
                </span>
                {showLabel && <span className="ms-1" style={labelStyle}>{item.Label}</span>}
              </Link>
            </li>
          );
        }
      });
  };

  return (
    <div
      id="sidebar-menu"
      style={{
        backgroundColor: "#e9e9e9",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: "relative",
      }}
    >
      <Link to="/home">
        <img
          src={isMobile ? logosmdark : logodark}
          alt="Logo"
          style={styles.logo}
        />
      </Link>
      <ul
        id="side-menu"
        className={
          !isMobile || !isCollapsed
            ? "metismenu list-unstyled"
            : "list-unstyled"
        }
      >
        {organization && organization.length > 0 ? (
          organization.map((org) => {
            if (org.id === "hospital") {
              return org.options
                .filter((option) =>
                  isSuperAdmin ||
                  permissions.some((perm) => perm.includes(option.id))
                )
                .map((option) => {
                  if (isMobile && isCollapsed) {
                    return (
                      <li key={option.id}>
                        <Link
                          to="#"
                          className="waves-effect"
                          onClick={(e) => {
                            e.preventDefault();
                            setMobileChildren(option.menu);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "10px",
                          }}
                        >
                          {option.icon && option.icon}
                        </Link>
                      </li>
                    );
                  } else {
                    const optionActive = isActiveItem(option);
                    const optionLabelStyle = {
                      color: optionActive ? activeColor : defaultLabelColor,
                      fontWeight: optionActive ? 700 : 500,
                    };
                    return (
                      <li key={option.id}>
                        <Link
                          to="#"
                          className="has-arrow waves-effect"
                          aria-expanded={optionActive ? "true" : "false"}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "10px 12px",
                            background: optionActive ? activeBackground : "transparent",
                            borderLeft: optionActive ? `3px solid ${activeColor}` : "3px solid transparent",
                            textDecoration: "none",
                          }}
                        >
                          {option.icon && <span style={{ color: optionActive ? activeColor : undefined }}>{option.icon}</span>}
                          <span className="ms-1" style={optionLabelStyle}>
                            {option.Label}
                          </span>
                        </Link>
                        {option.menu && option.menu.length > 0 && (
                          <ul className="sub-menu">
                            {renderMenuItems(option.menu, 1)}
                          </ul>
                        )}
                      </li>
                    );
                  }
                });
            }
            return null;
          })
        ) : (
          <li>No modules available</li>
        )}
      </ul>
      {isMobile && mobileChildren && (
        <div style={mobilePanelStyle}>
          <div style={backButtonStyle} onClick={() => setMobileChildren(null)}>
            &larr; Back
          </div>
          <ul className="list-unstyled">{renderMenuItems(mobileChildren, 1)}</ul>
        </div>
      )}

      {/* Footer: "Powered By" text followed by small logo */}
      <div
        style={{
          ...styles.footer,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 20, color: "#333", whiteSpace: "nowrap" }}>
          Powered By
        </span>
        <img
          src={mountlogo}
          alt="Mount Logo"
          style={{ width: 54, height: "auto", display: "block" }}
        />
      </div>
    </div>
  );
};

export default SidebarContent;
